import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import {
  ArrowLeft, Upload, Image as ImageIcon, X, Save, Trash2, Loader2,
  Eye, EyeOff, Plus, Shield,
} from "lucide-react";

const CATEGORY_OPTIONS = [
  { value: "",             label: "No category link"       },
  { value: "health",       label: "Health & Wellness"      },
  { value: "personal_care",label: "Personal Care"          },
  { value: "combo",        label: "Combos"                 },
  { value: "fashion",      label: "Fashion & Apparel"      },
  { value: "agriculture",  label: "Agriculture"            },
  { value: "education",    label: "Education"              },
];

/** SuperadminBanners — CRUD for home hero banners.
 *  Uploads go to backend persistent path (survive redeploys). Home page
 *  auto-merges these with the existing hardcoded slides.
 */
export default function SuperadminBanners() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [file, setFile] = useState(null);
  const [form, setForm] = useState({
    title: "",
    subtitle: "",
    primary_label: "Shop Wellness",
    primary_link: "/products?category=health",
    secondary_label: "Become an IBA",
    secondary_link: "/register",
    category: "",
    sort_order: 999,
    start_date: "",
    end_date: "",
  });
  const fileRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/superadmin/banners");
      setItems(data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load banners");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const pickFile = (f) => {
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) return toast.error("Image too large — max 5MB");
    if (!/^image\//.test(f.type)) return toast.error("Only image files allowed");
    setFile(f);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Please select a banner image");
    setCreating(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      Object.entries(form).forEach(([k, v]) => fd.append(k, String(v ?? "")));
      await api.post("/superadmin/banners", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Banner uploaded");
      setFile(null);
      if (fileRef.current) fileRef.current.value = "";
      setForm((p) => ({ ...p, title: "", subtitle: "", start_date: "", end_date: "" }));
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setCreating(false);
    }
  };

  const toggle = async (b) => {
    try {
      await api.patch(`/superadmin/banners/${b.id}`, { is_active: !b.is_active });
      toast.success(b.is_active ? "Banner disabled" : "Banner enabled");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const patchOrder = async (b, delta) => {
    try {
      await api.patch(`/superadmin/banners/${b.id}`, { sort_order: (b.sort_order || 0) + delta });
      await load();
    } catch (e) {
      // Non-blocking reorder — log for debugging but don't spam a toast for
      // rapid double-clicks or transient network hiccups.
      console.debug("Banner reorder failed:", e?.response?.data?.detail || e?.message);
    }
  };

  const remove = async (b) => {
    if (!window.confirm(`Delete banner "${b.title || 'untitled'}"? This is irreversible.`)) return;
    try {
      await api.delete(`/superadmin/banners/${b.id}`);
      toast.success("Banner deleted");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const linkForCategory = (c) => (c ? `/products?category=${c}` : form.primary_link);

  return (
    <div className="min-h-screen bg-ivory">
      <header className="bg-royal text-white border-b border-gold/30">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/superadmin" data-testid="sa-back" className="text-white/70 hover:text-gold inline-flex items-center gap-1.5 text-xs">
              <ArrowLeft size={14} /> Superadmin
            </Link>
            <span className="text-white/30">·</span>
            <div className="flex items-center gap-2">
              <Shield size={14} className="text-gold" />
              <span className="font-serif text-sm">Home Banners</span>
            </div>
          </div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-gold/70">
            {user?.user_code}
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10">
        {/* Uploader */}
        <section className="bg-white border border-line rounded-sm p-6 mb-10">
          <div className="flex items-center gap-2 mb-1">
            <Plus size={16} className="text-gold-deep" />
            <h2 className="font-serif text-2xl text-royal">Add New Banner</h2>
          </div>
          <p className="text-xs text-navy/55 mb-6">
            Uploaded banners appear on top of the existing home hero slider. All existing
            hardcoded slides remain untouched — this only adds to the rotation.
          </p>

          <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {/* File picker */}
            <div className="md:col-span-2">
              <label className="text-[10px] uppercase tracking-[0.3em] text-gold-deep">Banner Image *</label>
              <input
                type="file"
                accept="image/*"
                hidden
                ref={fileRef}
                data-testid="sa-banner-file"
                onChange={(e) => pickFile(e.target.files?.[0])}
              />
              <div
                onClick={() => fileRef.current?.click()}
                className={`mt-1 border-2 border-dashed rounded-sm p-4 cursor-pointer transition-colors ${
                  file ? "border-gold bg-gold/[0.04]" : "border-line hover:border-gold/60 bg-ivory/60"
                }`}
              >
                {file ? (
                  <div className="flex items-center gap-3">
                    <img
                      src={URL.createObjectURL(file)}
                      alt=""
                      className="w-32 h-20 object-cover border border-line rounded-sm"
                    />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm text-royal truncate">{file.name}</div>
                      <div className="text-[11px] text-navy/50">{(file.size / 1024).toFixed(0)} KB</div>
                    </div>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); setFile(null); if (fileRef.current) fileRef.current.value = ""; }}
                      className="text-navy/50 hover:text-rose p-2"
                      data-testid="sa-banner-file-clear"
                    >
                      <X size={16} />
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center justify-center gap-2 py-4 text-navy/60">
                    <Upload size={16} /> Click to choose banner image (≤ 5MB · PNG/JPG/WEBP)
                  </div>
                )}
              </div>
            </div>

            <Field label="Title (optional)" testid="sa-banner-title"
              value={form.title} onChange={(v) => setForm({ ...form, title: v })}
              placeholder="e.g., Diwali Special Combos" />
            <Field label="Subtitle (optional)" testid="sa-banner-subtitle"
              value={form.subtitle} onChange={(v) => setForm({ ...form, subtitle: v })}
              placeholder="e.g., Up to 30% off on wellness bundles" />

            <div>
              <label className="text-[10px] uppercase tracking-[0.3em] text-gold-deep">Category (auto-links Primary CTA)</label>
              <select
                data-testid="sa-banner-category"
                value={form.category}
                onChange={(e) => {
                  const c = e.target.value;
                  setForm((p) => ({
                    ...p,
                    category: c,
                    primary_link: c ? `/products?category=${c}` : p.primary_link,
                  }));
                }}
                className="mt-1 w-full border border-border px-3 py-2.5 text-sm rounded-sm focus:border-gold focus:outline-none focus:ring-2 focus:ring-gold/20 transition bg-white"
              >
                {CATEGORY_OPTIONS.map((o) => (<option key={o.value} value={o.value}>{o.label}</option>))}
              </select>
            </div>
            <Field label="Sort Order (lower = shown first)" testid="sa-banner-sort" type="number"
              value={form.sort_order} onChange={(v) => setForm({ ...form, sort_order: v })} />

            <Field label="Start Date (optional — leave blank for now)" testid="sa-banner-start" type="datetime-local"
              value={form.start_date} onChange={(v) => setForm({ ...form, start_date: v })} />
            <Field label="End Date (optional — leave blank for indefinite)" testid="sa-banner-end" type="datetime-local"
              value={form.end_date} onChange={(v) => setForm({ ...form, end_date: v })} />

            <Field label="Primary Button Label" testid="sa-banner-pri-label"
              value={form.primary_label} onChange={(v) => setForm({ ...form, primary_label: v })} />
            <Field label="Primary Button Link" testid="sa-banner-pri-link"
              value={form.primary_link} onChange={(v) => setForm({ ...form, primary_link: v })} mono />
            <Field label="Secondary Button Label" testid="sa-banner-sec-label"
              value={form.secondary_label} onChange={(v) => setForm({ ...form, secondary_label: v })} />
            <Field label="Secondary Button Link" testid="sa-banner-sec-link"
              value={form.secondary_link} onChange={(v) => setForm({ ...form, secondary_link: v })} mono />

            <div className="md:col-span-2 flex items-center justify-end pt-2">
              <button
                type="submit"
                disabled={creating || !file}
                data-testid="sa-banner-submit"
                className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-royal to-royal-deep text-white hover:from-royal-deep hover:to-royal disabled:opacity-50 disabled:cursor-not-allowed rounded-sm text-sm font-medium shadow-sm"
              >
                {creating ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                {creating ? "Uploading…" : "Upload Banner"}
              </button>
            </div>
          </form>
        </section>

        {/* Existing banners */}
        <section>
          <div className="flex items-end justify-between mb-4">
            <div>
              <h2 className="font-serif text-2xl text-royal">Current Banners</h2>
              <p className="text-xs text-navy/55 mt-1">
                {loading ? "Loading…" : `${items.length} custom banner${items.length === 1 ? "" : "s"}. Public home slider merges these with the 5 existing hardcoded hero shots.`}
              </p>
            </div>
          </div>

          {!loading && items.length === 0 ? (
            <div className="border border-dashed border-line rounded-sm py-16 text-center text-navy/50 bg-white">
              <ImageIcon size={32} className="mx-auto mb-3 text-navy/30" />
              No custom banners yet. Upload one above to add it to the home slider.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {items.map((b) => (
                <div key={b.id} data-testid={`sa-banner-item-${b.id}`} className="bg-white border border-line rounded-sm overflow-hidden">
                  <div className="relative aspect-[16/8] bg-ivory">
                    <img
                      src={b.image}
                      alt={b.title || "Banner"}
                      className={`w-full h-full object-cover ${!b.live_now ? "grayscale opacity-60" : ""}`}
                    />
                    {!b.is_active && (
                      <span className="absolute top-3 left-3 bg-navy/80 text-white text-[10px] tracking-widest uppercase px-2 py-1">
                        Disabled
                      </span>
                    )}
                    {b.is_active && !b.live_now && (
                      <span className="absolute top-3 left-3 bg-rose/90 text-white text-[10px] tracking-widest uppercase px-2 py-1">
                        Out of window
                      </span>
                    )}
                    <span className="absolute top-3 right-3 bg-white/95 text-royal text-[10px] font-mono px-2 py-1 rounded-sm">
                      #{b.sort_order}
                    </span>
                  </div>
                  <div className="p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="font-serif text-lg text-royal truncate">{b.title || "Untitled banner"}</div>
                        {b.subtitle && (
                          <div className="text-xs text-navy/55 mt-0.5 line-clamp-2">{b.subtitle}</div>
                        )}
                      </div>
                      <div className="text-right shrink-0">
                        <div className="text-[9px] uppercase tracking-widest text-gold-deep">Clicks · Views</div>
                        <div className="font-mono text-sm text-royal">
                          <span data-testid={`sa-banner-clicks-${b.id}`}>{b.click_count || 0}</span>
                          <span className="text-navy/40"> · </span>
                          <span data-testid={`sa-banner-views-${b.id}`}>{b.view_count || 0}</span>
                        </div>
                        <div className="text-[10px] text-navy/50 font-mono">
                          {b.view_count ? `${((b.click_count || 0) / b.view_count * 100).toFixed(1)}% CTR` : "—"}
                        </div>
                      </div>
                    </div>
                    <div className="mt-2 text-[10px] uppercase tracking-widest text-navy/40">
                      {b.category || "no category"} · {b.primary_label || "primary"} → {b.primary_link}
                    </div>
                    {(b.start_date || b.end_date) && (
                      <div className="mt-2 text-[11px] text-navy/60 font-mono bg-ivory/60 border border-line/70 rounded-sm px-2 py-1.5">
                        <span className="text-gold-deep uppercase text-[9px] tracking-widest mr-1">Schedule</span>
                        {b.start_date ? new Date(b.start_date).toLocaleString() : "always"}
                        <span className="text-navy/30"> → </span>
                        {b.end_date ? new Date(b.end_date).toLocaleString() : "indefinite"}
                      </div>
                    )}
                    <div className="mt-3 pt-3 border-t border-line flex items-center justify-between gap-2">
                      <button
                        onClick={() => toggle(b)}
                        data-testid={`sa-banner-toggle-${b.id}`}
                        className={`text-xs inline-flex items-center gap-1.5 px-2.5 py-1.5 border rounded-sm transition-colors ${
                          b.is_active
                            ? "border-navy/20 text-navy/70 hover:border-rose hover:text-rose"
                            : "border-olive/40 text-olive hover:border-olive"
                        }`}
                      >
                        {b.is_active ? <><EyeOff size={12} /> Disable</> : <><Eye size={12} /> Enable</>}
                      </button>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => patchOrder(b, -10)}
                          data-testid={`sa-banner-up-${b.id}`}
                          className="text-xs px-2.5 py-1.5 border border-line rounded-sm hover:border-gold text-navy/70 hover:text-royal"
                          title="Show earlier"
                        >
                          ↑
                        </button>
                        <button
                          onClick={() => patchOrder(b, 10)}
                          data-testid={`sa-banner-down-${b.id}`}
                          className="text-xs px-2.5 py-1.5 border border-line rounded-sm hover:border-gold text-navy/70 hover:text-royal"
                          title="Show later"
                        >
                          ↓
                        </button>
                        <button
                          onClick={() => remove(b)}
                          data-testid={`sa-banner-delete-${b.id}`}
                          className="text-xs inline-flex items-center gap-1 px-2.5 py-1.5 border border-rose/30 text-rose hover:bg-rose hover:text-white rounded-sm"
                        >
                          <Trash2 size={12} /> Delete
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testid, placeholder, mono }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-[0.3em] text-gold-deep">{label}</label>
      <input
        type={type}
        data-testid={testid}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className={`mt-1 w-full border border-border px-3 py-2.5 text-sm rounded-sm focus:border-gold focus:outline-none focus:ring-2 focus:ring-gold/20 transition ${mono ? "font-mono tracking-wide" : ""}`}
      />
    </div>
  );
}
